import { create } from 'zustand';
import { MindMapNode, ColorPalette, Language, SyncStatus } from '../types';
import { PALETTES, INITIAL_MARKDOWN } from '../constants';

interface MindMapState {
  // Core Data
  data: MindMapNode | null;
  history: MindMapNode[];
  historyTimestamps: number[];
  lastModified: number;
  rawMarkdown: string;
  fileName: string;
  isDirty: boolean;
  
  // Settings
  palette: ColorPalette;
  isDarkMode: boolean;
  language: Language;
  showEmoji: boolean;
  isMandalaMode: boolean;
  
  // View State
  selectedNode: MindMapNode | null;
  rearrangeTrigger: number;
  lastAddedNodeId: string | null;
  mountKey: number;
  initialView: { x: number; y: number; k: number } | undefined;
  
  // Auth & Sync
  userId: string | null;
  userName: string | null;
  currentMindmapId: string | null;
  syncStatus: SyncStatus;
  isAuthLoading: boolean;
  isAccessDenied: boolean;
  remoteConflict: { rawMarkdown: string; title: string; updatedAt: any } | null;
  fileSizeWarning: boolean;
  
  // UI State
  showCloudModal: boolean;
  isImportingImage: boolean;
  
  // Actions
  setData: (data: MindMapNode | null) => void;
  setHistory: (history: MindMapNode[]) => void;
  setHistoryTimestamps: (timestamps: number[]) => void;
  setLastModified: (timestamp: number) => void;
  setRawMarkdown: (markdown: string) => void;
  setFileName: (name: string) => void;
  setIsDirty: (isDirty: boolean) => void;
  
  setPalette: (palette: ColorPalette) => void;
  setIsDarkMode: (isDark: boolean) => void;
  setLanguage: (lang: Language) => void;
  setShowEmoji: (show: boolean) => void;
  setIsMandalaMode: (isMandala: boolean) => void;
  
  setSelectedNode: (node: MindMapNode | null) => void;
  setRearrangeTrigger: (trigger: number | ((prev: number) => number)) => void;
  setLastAddedNodeId: (id: string | null) => void;
  setMountKey: (key: number | ((prev: number) => number)) => void;
  setInitialView: (view: { x: number; y: number; k: number } | undefined) => void;
  
  setUserId: (id: string | null) => void;
  setUserName: (name: string | null) => void;
  setCurrentMindmapId: (id: string | null) => void;
  setSyncStatus: (status: SyncStatus) => void;
  setIsAuthLoading: (loading: boolean) => void;
  setIsAccessDenied: (denied: boolean) => void;
  setRemoteConflict: (conflict: { rawMarkdown: string; title: string; updatedAt: any } | null) => void;
  
  setShowCloudModal: (show: boolean) => void;
  setIsImportingImage: (isImporting: boolean) => void;

  // Complex Actions
  modifyData: (updater: (prev: MindMapNode) => MindMapNode) => void;
  handleUndo: () => void;
}

const getInitialMandalaMode = () => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('isMandalaMode');
    if (saved !== null) return saved === 'true';
  }
  return true;
};

export const useMindMapStore = create<MindMapState>((set, get) => ({
  data: null,
  history: [],
  historyTimestamps: [],
  lastModified: Date.now(),
  rawMarkdown: INITIAL_MARKDOWN,
  fileName: "Mindmap demo.md",
  isDirty: false,
  
  palette: PALETTES[0],
  isDarkMode: true,
  language: 'en',
  showEmoji: true,
  isMandalaMode: getInitialMandalaMode(),
  
  selectedNode: null,
  rearrangeTrigger: 0,
  lastAddedNodeId: null,
  mountKey: 0,
  initialView: undefined,
  
  userId: null,
  userName: null,
  currentMindmapId: null,
  syncStatus: 'local',
  isAuthLoading: true,
  isAccessDenied: false,
  remoteConflict: null,
  fileSizeWarning: false,
  
  showCloudModal: false,
  isImportingImage: false,
  
  setData: (data) => set({ data }),
  setHistory: (history) => set({ history }),
  setHistoryTimestamps: (historyTimestamps) => set({ historyTimestamps }),
  setLastModified: (lastModified) => set({ lastModified }),
  setRawMarkdown: (rawMarkdown) => set({ rawMarkdown }),
  setFileName: (fileName) => set({ fileName }),
  setIsDirty: (isDirty) => set({ isDirty }),
  
  setPalette: (palette) => set({ palette }),
  setIsDarkMode: (isDarkMode) => set({ isDarkMode }),
  setLanguage: (language) => set({ language }),
  setShowEmoji: (showEmoji) => set({ showEmoji }),
  setIsMandalaMode: (isMandalaMode) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('isMandalaMode', isMandalaMode.toString());
    }
    set({ isMandalaMode });
  },
  
  setSelectedNode: (selectedNode) => set({ selectedNode }),
  setRearrangeTrigger: (trigger) => set((state) => ({ rearrangeTrigger: typeof trigger === 'function' ? trigger(state.rearrangeTrigger) : trigger })),
  setLastAddedNodeId: (lastAddedNodeId) => set({ lastAddedNodeId }),
  setMountKey: (key) => set((state) => ({ mountKey: typeof key === 'function' ? key(state.mountKey) : key })),
  setInitialView: (initialView) => set({ initialView }),
  
  setUserId: (userId) => set({ userId }),
  setUserName: (userName) => set({ userName }),
  setCurrentMindmapId: (currentMindmapId) => set({ currentMindmapId }),
  setSyncStatus: (syncStatus) => set({ syncStatus }),
  setIsAuthLoading: (isAuthLoading) => set({ isAuthLoading }),
  setIsAccessDenied: (isAccessDenied) => set({ isAccessDenied }),
  setRemoteConflict: (remoteConflict) => set({ remoteConflict }),
  setFileSizeWarning: (fileSizeWarning) => set({ fileSizeWarning }),
  
  setShowCloudModal: (showCloudModal) => set({ showCloudModal }),
  setIsImportingImage: (isImportingImage) => set({ isImportingImage }),
  
  modifyData: (updater) => {
    const { data, history, historyTimestamps, lastModified } = get();
    if (!data) return;
    const newData = updater(data);
    if (newData === data) return;
    
    set({
      history: [...history, data],
      historyTimestamps: [...historyTimestamps, lastModified],
      data: newData,
      lastModified: Date.now(),
      isDirty: true
    });
  },
  
  handleUndo: () => {
    const { history, historyTimestamps } = get();
    if (history.length === 0) return;
    
    const previousState = history[history.length - 1];
    const previousTimestamp = historyTimestamps[historyTimestamps.length - 1] || Date.now();
    const newHistory = history.slice(0, -1);
    const newHistoryTimestamps = historyTimestamps.slice(0, -1);
    
    set({
      history: newHistory,
      historyTimestamps: newHistoryTimestamps,
      data: previousState,
      lastModified: previousTimestamp,
      isDirty: true
    });
  }
}));
